import type {
    AmplitudeApiKey,
    AppearanceMode,
    AppearanceSettings,
    AppearanceSource,
    Platform,
    Shortcut,
    Unsubscribe,
} from "./Platform";
import { amplitudeApiKeyForOrigin } from "./amplitudeApiKey";

interface BrowserCapabilities {
    appearance: AppearanceSource;
    shortcuts: Platform["shortcuts"];
    lifecycle: Platform["lifecycle"];
    /** The Amplitude project for the page's own origin. A page whose CSP lets it reach
     *  Amplitude (the web origin, the iOS shell) publishes one; the desktop publishes none.
     *  The iOS shell replaces it with its API origin's (`createIosPlatform`). */
    amplitudeApiKey: AmplitudeApiKey;
}

export function createBrowserCapabilities(userAgent = navigator.userAgent): BrowserCapabilities {
    const darkScheme = window.matchMedia("(prefers-color-scheme: dark)");
    const appearanceListeners = new Set<(next: AppearanceSettings) => void>();
    const environmentListeners = new Set<() => void>();
    const cleanups = new Set<Unsubscribe>();
    let selectedMode: AppearanceMode = "system";
    let textScale = 1;

    // AppearanceSource carries the user's requested preference. The provider resolves
    // `system` for the document, so an OS transition must never turn it into a fixed
    // light/dark preference here.
    const selectedAppearance = (): AppearanceSettings => ({ mode: selectedMode, textScale });
    const publishAppearance = () => {
        const next = selectedAppearance();
        appearanceListeners.forEach((listener) => listener(next));
    };
    const onSchemeChange = () => {
        if (selectedMode === "system") publishAppearance();
    };
    darkScheme.addEventListener("change", onSchemeChange);

    const appearance: AppearanceSource = {
        get mode() {
            return selectedMode;
        },
        get textScale() {
            return textScale;
        },
        subscribe(callback) {
            appearanceListeners.add(callback);
            return () => appearanceListeners.delete(callback);
        },
        set(next) {
            selectedMode = next.mode;
            textScale = next.textScale;
            publishAppearance();
        },
    };

    const isApple = /Macintosh|Mac OS X|iPhone|iPad|iPod/i.test(userAgent);
    const modifier = isApple ? "⌘" : "Ctrl";
    const register = (binding: Shortcut): Unsubscribe => {
        const modifiers = new Set(binding.modifiers ?? []);
        const onKeyDown = (event: KeyboardEvent) => {
            if (event.defaultPrevented) return;
            const primaryPressed = isApple ? event.metaKey : event.ctrlKey;
            if (primaryPressed !== modifiers.has("primary")) return;
            if (event.shiftKey !== modifiers.has("shift")) return;
            if (event.altKey !== modifiers.has("alt")) return;
            if (event.key.toLowerCase() !== binding.key.toLowerCase()) return;
            event.preventDefault();
            binding.handler();
        };
        window.addEventListener("keydown", onKeyDown);
        const unsubscribe = () => {
            window.removeEventListener("keydown", onKeyDown);
            cleanups.delete(unsubscribe);
        };
        cleanups.add(unsubscribe);
        return unsubscribe;
    };

    const onEnvironmentChange = (callback: () => void): Unsubscribe => {
        environmentListeners.add(callback);
        return () => environmentListeners.delete(callback);
    };

    return {
        appearance,
        shortcuts: { modifier, register },
        amplitudeApiKey: amplitudeApiKeyForOrigin(window.location.origin),
        lifecycle: {
            onEnvironmentChange,
            dispose() {
                darkScheme.removeEventListener("change", onSchemeChange);
                Array.from(cleanups).forEach((cleanup) => cleanup());
                appearanceListeners.clear();
                environmentListeners.clear();
            },
        },
    };
}
